/**
 * What the current turn is doing, derived from its parts.
 *
 * The label used to be stored: `onToolCall` wrote a message into React state
 * and the surfaces read it back. That state outlived the step that produced it,
 * because `onToolCall` fires only once a tool call is COMPLETE — so while the
 * model spent 142 seconds streaming the input to a canvas write, nothing
 * arrived to overwrite the previous step's message and the panel showed
 * "Loading CDASH domain: AE" for 1m45s.
 *
 * Deriving instead of storing removes the failure rather than resetting it:
 * there is no state left to go stale. The parts array already carries what is
 * needed — the AI SDK gives every tool call a part whose `state` advances
 * through input-streaming, input-available, output-available / output-error.
 *
 * `thinking` is the honest answer for a gap between steps. Callers pair it with
 * elapsed time; a stale label and a blank slot were both ruled out.
 */
import { isUserVisibleTool } from '@/lib/ai/tool-visibility'

/**
 * Human-readable description of what a tool call is doing.
 *
 * `input` is optional on purpose: while a call's input is still streaming
 * an argument is a prefix of itself, so callers pass nothing until the
 * input is complete and get the unparameterised description instead of a
 * truncated one.
 */
export function describeToolExecution(toolName: string, input?: Record<string, unknown>): string {
  const descriptions: Record<string, string | ((input?: Record<string, unknown>) => string)> = {
    // Living Codebook library
    codebook_list: () => 'Listing Living Codebooks',
    codebook_search: (i) => `Searching codebooks${i?.query ? `: ${String(i.query).slice(0, 40)}` : ''}`,
    codebook_get: (i) => `Loading codebook version${i?.version_id ? `: ${String(i.version_id).slice(0, 8)}…` : ''}`,
    codebook_form_get: (i) => `Loading codebook form${i?.form_id ? `: ${String(i.form_id).slice(0, 8)}…` : ''}`,
    codebook_diff: () => 'Comparing codebook versions',
    codebook_structure_get: () => 'Loading codebook structure',
    codebook_field_search: (i) => `Searching fields${i?.query ? `: ${String(i.query).slice(0, 40)}` : ''}`,
    codebook_field_get: (i) => {
      const name = i?.varname ? String(i.varname).slice(0, 40) : null
      return name ? `Loading field: ${name}` : 'Loading codebook field'
    },
    codebook_inventory: () => 'Building codebook inventory',
    codebook_rules_scan: () => 'Scanning codebook rules',
    codebook_integrity_check: () => 'Checking codebook integrity',
    // Standards tools
    cdash_lookup: (i) => `Looking up CDASH variable${i?.variable ? `: ${i.variable}` : ''}`,
    cdash_domain: (i) => `Loading CDASH domain${i?.domain ? `: ${i.domain}` : ''}`,
    sdtmig_lookup: (i) => `Looking up SDTMIG variable${i?.query ? `: ${i.query}` : ''}`,
    sdtmig_domain: (i) => `Loading SDTMIG domain${i?.domain ? `: ${i.domain}` : ''}`,
    sdtmig_codelist: (i) =>
      `Resolving SDTMIG→CT codelist${i?.variable_name ? `: ${i.variable_name}` : i?.codelist_code ? `: ${i.codelist_code}` : ''}`,
    sdtmig_notes: (i) => `Searching SDTMIG notes${i?.query ? `: ${i.query}` : ''}`,
    sdtm_ct_resolve: () => 'Resolving SDTM controlled terminology',
    sdtm_ct_codelist: () => 'Fetching SDTM codelist',
    rxnorm_lookup: (i) => `Searching RxNorm${i?.query ? `: ${i.query}` : ''}`,
    atc_lookup: () => 'Looking up ATC classification',
    snomed_lookup: (i) => `Searching SNOMED CT${i?.query ? `: ${i.query}` : ''}`,
    icd10_lookup: (i) => `Searching ICD-10${i?.query ? `: ${i.query}` : ''}`,
    loinc_lookup: (i) => `Searching LOINC${i?.query ? `: ${i.query}` : ''}`,
    // STRIDE tools
    stride_query: () => 'Querying STRIDE study library',
    stride_mdd_items: () => 'Fetching MDD standard items',
    stride_mdd_mappings: () => 'Fetching MDD item mappings',
    stride_ncdr: () => 'Fetching NCDR registry elements',
    stride_tables: () => 'Discovering STRIDE tables',
    stride_schema: () => 'Loading STRIDE schema',
    stride_test: () => 'Exploring STRIDE data',
    // Research tools
    ctgov_search: (i) => `Searching ClinicalTrials.gov${i?.query ? `: ${i.query}` : ''}`,
    ctgov_details: () => 'Fetching trial details',
    ctgov_endpoints: () => 'Loading trial endpoints',
    ctgov_eligibility: () => 'Loading eligibility criteria',
    pubmed_search: (i) => `Searching PubMed${i?.query ? `: ${i.query}` : ''}`,
    pubmed_article: () => 'Fetching article details',
    pubmed_fulltext: () => 'Loading full text',
    pubmed_related: () => 'Finding related articles',
    maude_search: () => 'Searching MAUDE device events',
    maude_device_events: () => 'Loading device event details',
    maude_event_counts: () => 'Counting device events',
    openfda_device_classify: (i) =>
      `Classifying device${i?.device_name ? `: ${String(i.device_name).slice(0, 40)}` : ' via openFDA'}`,
    openfda_device_classify_count: () => 'Counting openFDA classifications',
    openfda_device_510k: (i) =>
      `Searching openFDA 510(k)${i?.device_name ? `: ${String(i.device_name).slice(0, 40)}` : ''}`,
    openfda_device_510k_count: () => 'Counting openFDA 510(k) clearances',
    openfda_device_udi: (i) =>
      `Searching openFDA UDI${i?.term || i?.query ? `: ${String(i.term ?? i.query).slice(0, 40)}` : ''}`,
    openfda_device_recalls: () => 'Searching openFDA recalls',
    openfda_device_pma: () => 'Searching openFDA PMA approvals',
    openfda_device_registration: () => 'Searching openFDA registrations',
    // UI tools
    update_odm_xml: () => 'Generating ODM XML',
    show_form_preview: () => 'Rendering form preview',
    add_to_canvas: () => 'Writing to canvas',
    update_study_config: () => 'Updating study configuration',
    display_study: () => 'Loading study file',
    compare_odm: () => 'Comparing ODM versions',
    present_build_plan: () => 'Preparing build plan',
    get_active_process_status: () => 'Checking build status',
    validate_3ph_odm: () => 'Validating 3PH Cloud ODM',
    parse_crf_spec: () => 'Parsing CRF form checklist',
    // QMS tools (Qualio)
    qualio_list_documents: () => 'Searching Qualio documents',
    qualio_get_document: () => 'Loading QMS document',
    qualio_list_document_templates: () => 'Listing document templates',
    qualio_list_tags: () => 'Loading Qualio tags',
    qualio_list_events: () => 'Searching quality events',
    qualio_get_event: (i) => `Loading event ${i?.code ? String(i.code) : ''}`,
    qualio_list_event_templates: () => 'Listing event templates',
    qualio_list_change_controls: () => 'Loading change controls',
    qualio_list_user_training: () => 'Loading training records',
    qualio_list_users: () => 'Loading Qualio users',
    qualio_list_suppliers: () => 'Loading suppliers',
    qualio_get_supplier: () => 'Loading supplier detail',
    qualio_list_audit_trail: () => 'Querying audit trail',
    // General
    web_fetch: (i) => `Fetching${i?.url ? `: ${String(i.url).slice(0, 40)}...` : ' web content'}`,
  }

  const desc = descriptions[toolName]
  if (typeof desc === 'function') return desc(input)
  if (typeof desc === 'string') return desc
  return `Running ${toolName.replace(/_/g, ' ')}`
}


/**
 * How long the turn has been running, or null when there is nothing to say.
 *
 * Elapsed time is the honest filler for a gap between steps, so its only job
 * is to be readable and to stay a fixed width within a unit — the digits sit
 * in a pinned slot with `tabular-nums`, and a jump from `1m 5s` to `1m 12s`
 * must not reflow the row. Hence the zero padding on the trailing unit.
 */
export function formatElapsed(ms: number): string | null {
  if (!Number.isFinite(ms) || ms < 1_000) return null

  const totalSeconds = Math.floor(ms / 1_000)
  if (totalSeconds < 60) return `${totalSeconds}s`

  const totalMinutes = Math.floor(totalSeconds / 60)
  if (totalMinutes < 60) {
    return `${totalMinutes}m ${String(totalSeconds % 60).padStart(2, '0')}s`
  }

  return `${Math.floor(totalMinutes / 60)}h ${String(totalMinutes % 60).padStart(2, '0')}m`
}

/** A tool call that has been announced but has not produced a result yet. */
const UNRESOLVED_STATES: ReadonlySet<string> = new Set([
  'input-streaming',
  'input-available',
])

export type TurnActivity =
  | {
      kind: 'tool'
      tool: string
      /** `preparing` while the input streams, `running` once it is dispatched. */
      phase: 'preparing' | 'running'
      label: string
    }
  | { kind: 'thinking' }

const THINKING: TurnActivity = { kind: 'thinking' }

/**
 * The turn's current activity, or `thinking` when there is nothing determinate.
 *
 * Reads the EARLIEST unresolved tool call, so parallel calls cannot make the
 * label jump about and the pointer only ever advances as results arrive. A
 * step that errored counts as resolved — it is finished, and it must not hold
 * the slot any longer than a successful one does.
 *
 * Anything unrecognised resolves to `thinking`: a part shape this does not
 * understand should cost the label, never hold it. That is the safe direction
 * for the defect being fixed.
 */
export function deriveTurnActivity(parts: unknown): TurnActivity {
  if (!Array.isArray(parts)) return THINKING

  for (const part of parts) {
    const type = (part as { type?: unknown } | null)?.type
    if (typeof type !== 'string' || !type.startsWith('tool-')) continue

    const tool = type.slice('tool-'.length)
    if (!isUserVisibleTool(tool)) continue

    const state = (part as { state?: unknown }).state
    if (typeof state !== 'string' || !UNRESOLVED_STATES.has(state)) continue

    const phase = state === 'input-streaming' ? 'preparing' : 'running'
    const input = (part as { input?: unknown }).input
    return {
      kind: 'tool',
      tool,
      phase,
      // Arguments are withheld until the input is complete, so a label never
      // shows a truncated value that the next frame contradicts.
      label: describeToolExecution(
        tool,
        phase === 'running' && input && typeof input === 'object'
          ? (input as Record<string, unknown>)
          : undefined,
      ),
    }
  }

  return THINKING
}
